import React from 'react';
import { Input, Tag } from 'antd';
import type { AiResource, AiResourceConfig } from '../../../types';

/**
 * 密码输入组件（用于 API 密钥）
 */
export const PasswordInput: React.FC<any> = ({ value, onChange, ...props }) => (
  <Input.Password value={value} onChange={onChange} {...props} />
);

/**
 * 渲染提供商标签
 */
export const renderProvider = (provider: string) => (
  <Tag color="blue">{provider}</Tag>
);

/**
 * 渲染标签列表
 */
export const renderTags = (tags?: string): React.ReactNode => {
  if (!tags) return '-';
  return (
    <>
      {tags.split(',').map((tag) => (
        <Tag key={tag} style={{ marginBottom: 4 }}>
          {tag.trim()}
        </Tag>
      ))}
    </>
  );
};

/**
 * 渲染启用状态
 */
export const renderEnabled = (enabled: boolean): React.ReactNode => (
  <Tag color={enabled ? 'green' : 'red'}>
    {enabled ? '启用' : '禁用'}
  </Tag>
);

/**
 * 解析资源的 config 字段（JSON 字符串 → 对象）
 */
export const parseResourceConfig = (
  config?: string | AiResourceConfig | null
): AiResourceConfig => {
  if (!config) return {};
  if (typeof config === 'object') return config;
  try {
    const parsed = JSON.parse(config);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
};

/**
 * 获取表单初始值（将 config JSON 中的 qwenVersion 提取到表单字段）
 */
export const getFormInitialValues = (editingResource?: AiResource | null) => {
  if (editingResource) {
    const config = parseResourceConfig(editingResource.config);
    return {
      ...editingResource,
      modelTier: editingResource.modelTier ?? 'medium',
      // 将 config 中的 qwenVersion 提取为表单字段
      qwenVersion: config.qwenVersion || undefined,
    };
  }
  return {
    enabled: true,
    priority: 0,
    provider: 'qwen',
    modelTier: 'medium' as const,
    qwenVersion: '3.8' as const,
  };
};

/**
 * 准备保存的资源数据（将 qwenVersion 等表单字段序列化到 config JSON 中）
 */
export const prepareResourceForSave = (
  formValues: Record<string, any>,
  isEditing: boolean,
  existingResource?: AiResource | null
): Record<string, any> => {
  const { qwenVersion, ...restValues } = formValues;

  // 解析已有的 config（保留其他配置项）
  const existingConfig = isEditing && existingResource
    ? parseResourceConfig(existingResource.config)
    : {};

  // 构建新的 config
  const newConfig: AiResourceConfig = { ...existingConfig };

  if (restValues.provider === 'qwen' && qwenVersion) {
    newConfig.qwenVersion = qwenVersion;
  } else {
    // 非 qwen 提供商时清除 qwenVersion
    delete newConfig.qwenVersion;
  }

  // 如果有 config 内容则序列化为 JSON 字符串，否则设为 null
  const configStr = Object.keys(newConfig).length > 0
    ? JSON.stringify(newConfig)
    : null;

  return {
    ...restValues,
    config: configStr,
  };
};
